import { Castle, Plane, Flower2, Music, Users, Heart } from "lucide-react";
import PageHero, { ScrollCta } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import LeadForm from "@/components/LeadForm";

const IMG = {
  palace: "https://images.unsplash.com/photo-1756190564669-215843660e93?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  mandap: "https://images.unsplash.com/photo-1587271636175-90d58cdad458?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  ballroom: "https://images.unsplash.com/photo-1587271407850-8d438ca9fdf2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  arch: "https://images.unsplash.com/photo-1761472606347-bfebc5a3e546?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  beach: "https://images.unsplash.com/photo-1718152219044-edc915854e70?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  sunset: "https://images.unsplash.com/photo-1725044546720-dd4f3dd17b7a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  table: "https://images.unsplash.com/photo-1653821355226-6def361cc7ab?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
};

const SERVICES = [
  {
    icon: Castle,
    title: "Venue Scouting & Curation",
    desc: "Exclusive access and negotiation for heritage palaces, five-star luxury resorts, and bespoke destination venues.",
    img: IMG.arch,
  },
  {
    icon: Plane,
    title: "Logistics & Guest Hospitality",
    desc: "Seamless end-to-end management of flight bookings, airport transfers, room allocations, and personalized welcome hampers for your guests.",
    img: IMG.table,
  },
  {
    icon: Flower2,
    title: "Thematic Decor & Set Design",
    desc: "Bespoke stage fabrication, floral arrangements, and lighting setups tailored to your specific aesthetic and wedding themes (Mehendi, Sangeet, Pheras, Reception).",
    img: IMG.ballroom,
  },
  {
    icon: Music,
    title: "Entertainment & Artist Management",
    desc: "Sourcing and coordinating live bands, celebrity performers, DJs, and traditional folk artists.",
    img: IMG.mandap,
  },
  {
    icon: Users,
    title: "Dedicated On-Ground Shadow Team",
    desc: "A highly trained, on-site execution team managing the minute-by-minute itinerary, ensuring the family remains completely stress-free.",
    img: IMG.beach,
  },
];

const SETTINGS = [
  { img: IMG.palace, label: "Royal Palace Celebration" },
  { img: IMG.sunset, label: "Serene Beachfront Ceremony" },
  { img: IMG.ballroom, label: "Exclusive Resort Takeover" },
];

const WEDDING_SERVICES = [
  "Venue Scouting & Curation",
  "Logistics & Guest Hospitality",
  "Thematic Decor & Set Design",
  "Entertainment & Artist Management",
  "Dedicated On-Ground Shadow Team",
];

export default function Weddings() {
  return (
    <div data-testid="weddings-page">
      <PageHero
        eyebrow="Luxury Wedding Division"
        title={["Destination", "Weddings."]}
        sub="Your wedding should be a celebration, not a logistical challenge. Evolve Elite's luxury wedding division specializes in curating breathtaking destination weddings across premium locations. Whether you envision a royal palace celebration, a serene beachfront ceremony, or an exclusive resort takeover, our team orchestrates every detail with absolute perfection and cultural authenticity."
        tint="#FBF7F0"
      >
        <ScrollCta href="#plan" label="Plan Your Dream Wedding" testId="weddings-plan-btn" />
      </PageHero>

      {/* SETTINGS */}
      <section className="py-16 sm:py-24" data-testid="weddings-settings-section">
        <div className="container-x">
          <Reveal>
            <p className="eyebrow">Three Signature Settings</p>
            <h2 className="mt-3 max-w-2xl font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl lg:text-5xl">
              Palace. Shoreline. Resort.
            </h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-3">
            {SETTINGS.map((s, i) => (
              <Reveal key={s.label} delay={i * 0.08}>
                <div className="group relative overflow-hidden rounded-3xl" data-testid={`weddings-setting-${i + 1}`}>
                  <img
                    src={s.img}
                    alt={s.label}
                    className="aspect-[3/4] w-full object-cover transition-transform duration-700 group-hover:scale-[1.05]"
                    loading="lazy"
                  />
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-navy/85 via-navy/30 to-transparent p-6 pt-16">
                    <p className="font-display text-xl font-extrabold text-white">{s.label}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES — alternating */}
      <section className="border-y border-slate-200 bg-[#FBF7F0] py-16 sm:py-24 lg:py-28" data-testid="weddings-services-section">
        <div className="container-x">
          <Reveal>
            <p className="eyebrow">Our Premium Offerings</p>
            <h2 className="mt-3 max-w-2xl font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl lg:text-5xl">
              Every detail, orchestrated.
            </h2>
          </Reveal>
          <div className="mt-14 space-y-16 sm:space-y-20">
            {SERVICES.map((s, i) => (
              <Reveal key={s.title} delay={0.05}>
                <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-12 lg:gap-14" data-testid={`weddings-service-${i + 1}`}>
                  <div className={`lg:col-span-5 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                    <div className="group relative overflow-hidden rounded-3xl">
                      <img
                        src={s.img}
                        alt={s.title}
                        className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                        loading="lazy"
                      />
                      <span className="absolute left-5 top-5 rounded-full bg-white/95 px-4 py-1.5 font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-navy backdrop-blur">
                        0{i + 1}
                      </span>
                    </div>
                  </div>
                  <div className={`lg:col-span-7 ${i % 2 === 1 ? "lg:order-1" : ""}`}>
                    <s.icon className="h-9 w-9 text-amber-600" />
                    <h3 className="mt-5 font-display text-2xl font-extrabold tracking-tight text-navy sm:text-3xl">{s.title}</h3>
                    <p className="mt-4 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg">{s.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PLAN FORM */}
      <section id="plan" className="grain relative bg-navy py-16 sm:py-24" data-testid="weddings-plan-section">
        <div className="container-x relative z-10 grid grid-cols-1 items-center gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <p className="eyebrow !text-gold">Private Consultation</p>
            <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl">
              Plan Your Dream Wedding.
            </h2>
            <p className="mt-5 max-w-md text-white/70">
              Tell us the season, the guest count and the setting you dream of. Our wedding division returns with
              curated venues, a mood direction and a transparent budget — then takes the planning off your hands.
            </p>
            <p className="mt-6 flex items-center gap-2 text-sm font-bold text-gold">
              <Heart className="h-4 w-4" /> Discreet, family-first planning from the very first call
            </p>
          </Reveal>
          <Reveal delay={0.12} className="lg:col-span-7">
            <div className="rounded-3xl border border-white/10 bg-white p-6 shadow-2xl sm:p-8" data-testid="weddings-plan-form-card">
              <LeadForm
                source="weddings-page"
                fields={["name", "phone", "email", "service", "message"]}
                services={WEDDING_SERVICES}
                cta="Plan Your Dream Wedding"
                idPrefix="weddings-plan"
              />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
